import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AutocompleteConfig } from '@card-software/library';
import { debouncedServerSearch } from '@card-software/nexus-utilities';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { Observable, Subject, catchError, map, of, throwError } from 'rxjs';
import { BusinessEntityType } from '../models/business';
import { BusinessProxyService } from '../proxies/business.proxy.service';

/**
 * One row in the provider type-ahead.
 *
 * `label` is precomputed rather than templated: {@link AutocompleteConfig.optionLabel} names a single
 * field on the suggestion, so it cannot join code and name itself.
 */
export interface ProviderSuggestion {
  id: string;
  label: string;
}

/**
 * What a provider filter control actually holds. A picked suggestion is the object; a filter restored
 * from the URL or session arrives as the bare id string, because that is all the URL carries.
 */
export type ProviderFilterValue = ProviderSuggestion | string | null;

/** How many rows one keystroke fetches. Small on purpose — the list is scanned, not paged. */
const SUGGESTION_LIMIT = 8;

/**
 * The provider type-ahead shared by every AP search screen (AP documents, payments, payment requests,
 * advances, advance requests, PO settlement).
 *
 * <p>These filters were selects loading the first 200 providers, which is both a wrong answer past 200
 * providers and a poor way to find one. The APIs match ProviderReference exactly by id, so the officer
 * still has to pick a real row — searching is how they find it, not free-text matching.</p>
 */
@Injectable({ providedIn: 'root' })
export class ProviderFilterService {
  private readonly businessService = inject(BusinessProxyService);

  /**
   * A config for ONE filter panel.
   *
   * <p>Never share the returned object between screens: the panel's completeMethod writes results
   * straight onto `suggestions`, so a shared instance would let one screen's search replace another's
   * open list.</p>
   *
   * <p>`destroyRef` belongs to the calling screen and ends this config's search subscription with it.</p>
   */
  createConfig(destroyRef: DestroyRef): AutocompleteConfig {
    const queries$ = new Subject<string>();

    const config: AutocompleteConfig = {
      suggestions: [],
      optionLabel: 'label',
      // The panel keeps the whole suggestion in the control and reads this field only when writing the
      // URL — so the control holds an object, and the URL holds the id.
      optionValue: 'id',
      completeMethod: (event: AutoCompleteCompleteEvent) => queries$.next(event.query),
      resolveById: (id: string) => this.resolveProvider(id),
      // The dropdown arrow fires completeMethod with an empty query, which lists the first providers —
      // so the control is still browsable by someone who does not know what to type.
      dropdown: true,
      // Typing a name that is never picked must leave the filter empty rather than send an unmatched
      // string the API would compare against an id and silently match nothing.
      forceSelection: true,
    };

    // The real <p-autoComplete> lives inside the filter panel, so there is no element to hang
    // ServerSearchDirective on: the operator is wired by hand here, and this subscription is the one
    // thing that has to be released — hence the caller's DestroyRef.
    //
    // minLength stays at its default 0 on purpose: `dropdown: true` searches with an empty query.
    queries$
      .pipe(
        debouncedServerSearch((query) => this.searchProviders(query)),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((suggestions) => (config.suggestions = suggestions));

    return config;
  }

  /** Normalises whatever the panel hands back into the id the APIs match on. */
  toProviderId(value: unknown): string | null {
    if (!value) return null;
    if (typeof value === 'string') return value || null;
    const suggestion = value as Partial<ProviderSuggestion>;
    return suggestion.id ?? null;
  }

  private resolveProvider(id: string): Observable<ProviderSuggestion | null> {
    return this.businessService.getBusinessById(id).pipe(
      map((response) => {
        const business = response.data;
        return business?.id ? this.toSuggestion(business.id, business.code, business.name) : null;
      }),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private searchProviders(query: string): Observable<ProviderSuggestion[]> {
    return (
      this.businessService
        // matchAny: one typed term is tried against BOTH name and code, so "ACME" and "P-001" both work.
        .search({ name: query, code: query, businessTypes: [BusinessEntityType.Provider] }, 1, SUGGESTION_LIMIT, undefined, undefined, true)
        .pipe(
          map((response) =>
            (response.data?.items ?? [])
              .filter((provider) => !!provider.id)
              .map((provider) => this.toSuggestion(provider.id!, provider.code, provider.name)),
          ),
        )
    );
  }

  private toSuggestion(id: string, code?: string | null, name?: string | null): ProviderSuggestion {
    return { id, label: code ? `${code} — ${name ?? ''}`.trim() : (name ?? id) };
  }
}
